import React, {
  useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo,
} from 'react';
import {
  Box, Typography, Paper, TableContainer, Table, TableBody, TableHead, TableFooter, TableRow, TableCell,
  Alert, CircularProgress, Chip, Tooltip, Modal, Fade, IconButton, Stack, Dialog, Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  InboxOutlined as EmptyIcon,
  RefreshRounded as RefreshIcon,
  ReceiptLongOutlined as LedgerIcon,
  TableViewRounded as ExcelIcon,
  PictureAsPdfRounded as PdfIcon,
  OpenInFullRounded as ExpandIcon,
  CloseFullscreenRounded as CollapseIcon,
} from '@mui/icons-material';
import { saveAs } from 'file-saver';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../redux/actions';
import { formatCurrency } from '../../utils/format';
import ExpensesDetail from '../../../Finance/ExpensesDetail';

const PAGE_SIZE = 20;
const TABLE_MAX_HEIGHT = 480;
const SCROLL_TOP_THRESHOLD = 80;
// เส้นแบ่งกลุ่มวันที่ในสมุด — เข้มกว่าเส้นแถวปกติ ตีบนแถวแรกของแต่ละวัน
const DATE_GROUP_BORDER = '2px solid #94a3b8';
const dayKey = (d) => new Date(d).toLocaleDateString('th-TH');

// ตารางรวมรายรับ-รายจ่ายบัญชีบริษัทเป็นสมุดบัญชีเดียว (แทนที่ CustomerCashFlowList
// สองการ์ดแยก income/expense เดิม) — 1 payment ที่มีหลาย expense จะเห็นหลายแถวที่เลขที่
// เอกสารซ้ำกัน ไม่ group/rowSpan (ตาม pattern เดียวกับ LoanLedgerTable ที่มีอยู่แล้วในหน้า
// loan-pairs)
//
// แสดงผลจากเก่าไปใหม่ (บนสุด=เก่าสุด, ล่างสุด=ใหม่สุด — แบบสมุดบัญชีจริง) แต่ดึงข้อมูลจาก
// backend เป็นชุดๆ ตามลำดับใหม่->เก่า (date desc, เหมือนเดิม) แล้ว reverse แต่ละชุดก่อนเอาไป
// "แปะไว้ด้านบน" ของแถวที่มีอยู่ (prepend) — เลือกทำแบบนี้แทนที่จะ query แบบ date asc ตรงๆ
// เพราะตอนเปิดหน้าครั้งแรกอยากให้เห็นรายการล่าสุดทันที (เลื่อนไปล่างสุดอัตโนมัติ) โดยไม่ต้อง
// โหลดข้อมูลทั้งหมดตั้งแต่อดีตมาก่อน (ตาราง "รายจ่าย" คือ payment เกือบทั้งหมดของทั้งบริษัท
// อาจมีเยอะมาก) — เลื่อนขึ้นด้านบนถึงจะค่อยโหลดรายการเก่ากว่ามาแปะเพิ่มด้านบนต่อไปเรื่อยๆ
// (infinite-scroll กลับทิศจากเดิม) การคำนวณคงเหลือสะสม/แทรกแถวคั่น "กำหนดยอดคงเหลือ" ยังทำ
// บนลำดับ "ใหม่->เก่า" ภายในเหมือนเดิมทุกประการ (ดูด้านล่าง) ค่อย reverse ตอนจะเอาไปแสดงผล
// เท่านั้น — ต้องดึง/ประมวลผลตามลำดับ date เดียวกับที่ dateFrom ใช้ตัดสิน "ก่อน/หลังกำหนด
// ยอดตั้งต้น" เสมอ ไม่งั้นแถวก่อน/หลัง cutoff จะแทรกสลับกัน ใส่แถวคั่นจุดเดียวไม่ได้
//
// prepend มาแล้ว scroll position ต้องคงที่ (ไม่ให้เนื้อหาที่กำลังดูอยู่กระโดด) — ใช้
// useLayoutEffect ปรับ scrollTop ชดเชยความสูงที่เพิ่มขึ้นด้านบน (pendingScrollActionRef บอกว่า
// รอบนี้ต้อง "เลื่อนไปล่างสุด" (โหลดครั้งแรก) หรือ "รักษาตำแหน่งเดิม" (prepend จาก scroll ขึ้น)
// — ต้อง set ref นี้ค่าก่อนเรียก setRows เสมอ เพราะโปรเจกต์นี้ยังใช้ React 17 (ไม่มี automatic
// batching นอก event handler) setState แต่ละตัวอาจ trigger re-render แยกกันทันทีระหว่างบรรทัด
// (gotcha เดียวกับที่เขียนไว้ใน LoanLedgerTable.js's useLayoutEffect)
//
// คงเหลือสะสมคำนวณฝั่งนี้ล้วนๆ ไม่พึ่ง backend — เพราะ infinite-scroll pagination ทำให้
// คำนวณคงเหลือสะสมจริงแบบ query เดียวยาก (ต้องรู้ยอดสะสมทั้งหมดตั้งแต่อดีต ไม่ใช่แค่หน้าที่
// กำลังโหลด) — รองรับ "ปรับปรุงยอด" ได้หลายครั้ง (adjustments prop = ประวัติทุกครั้ง เรียงเก่า->
// ใหม่) แต่ละครั้ง = 1 segment ที่มี anchor (balance) ของตัวเอง + 1 แถว divider "ยอดตั้งต้น":
// - segment ใหม่สุด (date >= cutoff ล่าสุด): anchor = balance ครั้งล่าสุด + net ของรายการหลังจากนั้น
// - เดินถอย (ใหม่->เก่า) เจอแถวแรกที่ date < cutoff[i] → แทรก divider ของครั้งที่ i (โชว์ balance
//   ที่บันทึกไว้ + หมายเหตุ) แล้ว reset anchor เป็นของ segment ถัดไป (ที่เก่ากว่า):
//     segment i-1 = balance[i-1] + netAfter[i-1] - netAfter[i]  (นับต่อจากยอดครั้งก่อน ไม่รีเซ็ตเป็น 0)
//     segment เก่าสุด (ก่อนปรับปรุงครั้งแรก) = totalNet - netAfter[0]  (net ล้วนนับจาก 0)
//   netAfter[i] = ยอดสุทธิของ payment ที่ date >= cutoff[i] — backend ส่งมาใน data.boundaries
// - แถวก่อน cutoff ล่าสุดทุกแถวถือว่ายืนยัน OCR แล้วอัตโนมัติ (ดู isVerified / latestCutoff)
// runningBalanceRef + pendingDividersRef เก็บสถานะไว้ข้ามหน้า infinite-scroll
export function CompanyLedgerTable({
  customerId, dateFrom, openingBalance, adjustments, showStatusColumn,
}) {
  const dispatch = useDispatch();
  const scrollRef = useRef(null);
  const runningBalanceRef = useRef(0);
  // divider "ยอดตั้งต้น" ที่ยังไม่ถูกแทรก เรียงใหม่->เก่า (ปรับปรุงยอดได้หลายครั้ง = หลาย divider)
  // แต่ละอันมี resetTo = ค่า runningBalance ที่ต้องเซ็ตเมื่อข้าม divider นั้นไปหาแถวที่เก่ากว่า
  // (= anchor ของช่วงถัดไป ให้คงเหลือสะสมของช่วงนั้นเริ่มนับต่อจากยอดที่บันทึกไว้ ไม่ใช่นับจาก 0 ใหม่)
  const pendingDividersRef = useRef([]);
  const realCountRef = useRef(0);
  const pendingScrollActionRef = useRef(null); // 'bottom' | 'preserve' | null
  const prevScrollHeightRef = useRef(0);
  // mirror ของ rows state — export ต้องอ่านค่าล่าสุดหลังโหลดครบทุกหน้า (state ใน closure จะ stale)
  const rowsRef = useRef([]);

  const [fullscreen, setFullscreen] = useState(false);
  const [exporting, setExporting] = useState(false);

  // ประวัติการปรับปรุงยอด normalize เป็น segment เรียงเก่า->ใหม่ ({ date, balance, note })
  // fallback: ถ้าไม่ได้ส่ง adjustments มาแต่มี dateFrom (โค้ดเก่า/บัญชียังไม่มีประวัติ) = 1 segment
  // ใช้ adjSig (string) เป็น dep ของ useMemo แทน array identity กัน re-compute ทุก render
  const adjSig = JSON.stringify(
    (adjustments || []).map((a) => [a.effective_date, a.balance, a.note]),
  );
  const segments = useMemo(() => {
    const src = (adjustments && adjustments.length)
      ? adjustments.map((a) => ({ date: a.effective_date, balance: Number(a.balance || 0), note: a.note || '' }))
      : (dateFrom ? [{ date: dateFrom, balance: Number(openingBalance || 0), note: '' }] : []);
    return [...src].sort((x, y) => new Date(x.date) - new Date(y.date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjSig, dateFrom, openingBalance]);
  const apiBoundaries = useMemo(
    () => segments.map((s) => new Date(s.date).toISOString()),
    [segments],
  );
  // cutoff ล่าสุด — ใช้กับกฎ "รายการก่อน cutoff ล่าสุด = ยืนยัน OCR แล้วอัตโนมัติ"
  const latestCutoff = segments.length ? segments[segments.length - 1].date : null;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  // ยอดรวมรายรับ/รายจ่ายทั้งหมด (จาก backend) — โชว์ในแถวสรุปท้ายตาราง (เดิมอยู่ในการ์ด
  // "รายรับรวม"/"รายจ่ายรวม" ที่หน้า CompanyAccount ย้ายมาไว้ที่คอลัมน์ของมันเองแทน)
  const [incomeTotal, setIncomeTotal] = useState(0);
  const [expenseTotal, setExpenseTotal] = useState(0);

  // ดับเบิลคลิกแถว → เปิด modal "รายละเอียดรายการเบิกจ่าย" เหมือนกับ ExpensesTableApplove.js/
  // LoanLedgerTable.js (component เดียวกัน <ExpensesDetail propId={...}> ไม่ใช่ก็อปโค้ดมาเขียน
  // ใหม่) — propId ต้องเป็น row.expensesId (= expenses_list.expenses_id ฝั่ง backend, reference
  // จริงไปยัง Expenses collection) ห้ามใช้ row.id (= expenses_list._id ของ subdocument เอง ใช้
  // เป็น React key เท่านั้น) เด็ดขาด ไม่งั้น expensesGet(id) จะ 404
  const [detailExpenseId, setDetailExpenseId] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const openDetail = (id) => {
    setDetailExpenseId(id);
    setDetailModalOpen(true);
  };
  const closeDetail = () => setDetailModalOpen(false);

  // เดิมฝั่งรายรับ (direction==='income') ถือว่ายืนยันแล้วเสมอไม่ต้องเช็ค OCR — ตอนนี้
  // ต้องยืนยันเหมือนฝั่งรายจ่ายแล้ว (ผู้ใช้อยากให้ครอบคลุมรายรับด้วย) เหลือแค่กฎ "ก่อน
  // กำหนดยอดตั้งต้นถือว่ายืนยันแล้วอัตโนมัติ" ที่ยังใช้ทั้งสองทิศทางเหมือนเดิม
  const isVerified = useCallback(
    (row) => (latestCutoff && new Date(row.date) < new Date(latestCutoff))
      || !!row.ocr_verified_company_account,
    [latestCutoff],
  );

  // สถานะคอลัมน์สุดท้าย — 3 แบบ:
  //  ยืนยันแล้ว   = isVerified (ยืนยัน OCR แล้ว หรือรายการก่อนวันกำหนดยอดตั้งต้น)
  //  ไม่พบในรูป   = ocr_not_found (เคยผ่าน OCR แล้วเทียบไม่เจอตัวมันในรูป not_found/mismatched)
  //                — จัดการต่อที่การ์ด "payment ในระบบที่ไม่พบในธนาคาร"
  //  ยังไม่ยืนยัน  = ยังไม่เคยเอาไป OCR เทียบเลย
  const renderStatusChip = (row) => {
    if (isVerified(row)) {
      return <Chip size="small" color="success" variant="outlined" label="ยืนยันแล้ว" sx={{ fontWeight: 650 }} />;
    }
    if (row.ocr_not_found) {
      return (
        <Tooltip title={'OCR เทียบแล้วไม่พบรายการนี้ในรูป — จัดการต่อที่การ์ด "payment ในระบบที่ไม่พบในธนาคาร"'}>
          <Chip size="small" color="error" variant="outlined" label="ไม่พบในรูป" sx={{ fontWeight: 650 }} />
        </Tooltip>
      );
    }
    return <Chip size="small" color="warning" variant="outlined" label="ยังไม่ยืนยัน" sx={{ fontWeight: 650 }} />;
  };

  const loadPage = useCallback(async (pageToLoad, append) => {
    if (!customerId) return;
    if (append) setLoadingMore(true); else setLoading(true);
    setError('');
    if (append && scrollRef.current) {
      prevScrollHeightRef.current = scrollRef.current.scrollHeight;
    }
    try {
      const data = await dispatch(actions.listCustomerCashFlowLedger({
        customerId, page: pageToLoad, size: PAGE_SIZE, boundaries: apiBoundaries,
      }));
      if (!append) {
        setIncomeTotal(data.incomeTotal || 0);
        setExpenseTotal(data.expenseTotal || 0);
        realCountRef.current = 0;

        const totalNet = Number(data.incomeTotal || 0) - Number(data.expenseTotal || 0);
        // net หลัง cutoff ต่อ boundary (key = ISO string ที่ frontend ส่งไป, backend echo กลับมา)
        const netByDate = new Map(
          (data.boundaries || []).map((b) => [
            String(b.date),
            Number(b.incomeAfter || 0) - Number(b.expenseAfter || 0),
          ]),
        );
        const segAsc = segments.map((s, i) => ({
          ...s,
          netAfter: netByDate.has(apiBoundaries[i]) ? netByDate.get(apiBoundaries[i]) : 0,
        }));

        if (segAsc.length === 0) {
          runningBalanceRef.current = totalNet;
          pendingDividersRef.current = [];
        } else {
          const k = segAsc.length;
          // anchor แถวใหม่สุด = ยอดที่บันทึกไว้ตอนปรับปรุงครั้งล่าสุด + net ของรายการหลังจากนั้น
          runningBalanceRef.current = segAsc[k - 1].balance + segAsc[k - 1].netAfter;
          // divider ใหม่->เก่า; resetTo = anchor ของช่วง "ที่เก่ากว่า divider นี้"
          //   ช่วง i-1 (มี anchor) → balance ครั้งก่อน + net เฉพาะช่วงนั้น (netAfter[i-1] - netAfter[i])
          //   ช่วงเก่าสุด (ก่อนปรับปรุงครั้งแรก, ไม่มี anchor) → net ล้วนนับจาก 0
          pendingDividersRef.current = [];
          for (let i = k - 1; i >= 0; i -= 1) {
            const resetTo = i > 0
              ? segAsc[i - 1].balance + segAsc[i - 1].netAfter - segAsc[i].netAfter
              : totalNet - segAsc[0].netAfter;
            pendingDividersRef.current.push({
              date: segAsc[i].date, balance: segAsc[i].balance, note: segAsc[i].note, resetTo,
            });
          }
        }
      }
      const withBalance = [];
      const flushDivider = () => {
        const d = pendingDividersRef.current.shift();
        withBalance.push({
          id: `divider-${d.date}`, isDivider: true, date: d.date, balance: d.balance, note: d.note,
        });
        runningBalanceRef.current = d.resetTo;
      };
      data.rows.forEach((row) => {
        while (
          pendingDividersRef.current.length
          && new Date(row.date) < new Date(pendingDividersRef.current[0].date)
        ) {
          flushDivider();
        }
        const balance = runningBalanceRef.current;
        const signedAmount = row.direction === 'income' ? Number(row.amount || 0) : -Number(row.amount || 0);
        runningBalanceRef.current -= signedAmount;
        withBalance.push({ ...row, balance });
      });
      realCountRef.current += data.rows.length;
      // โหลดครบทุกแถวจริงแล้ว แต่ยังมี divider ค้าง (ปรับปรุงยอดตั้งแต่ก่อนมีรายการแรกในระบบ) —
      // แปะ divider ที่เหลือทั้งหมดไว้ท้ายสุด (เรียงใหม่->เก่าอยู่แล้ว)
      if (realCountRef.current >= data.total) {
        while (pendingDividersRef.current.length) {
          flushDivider();
        }
      }
      // reverse ก่อนแสดงผล (ประมวลผลด้านบนเป็นลำดับใหม่->เก่าเสมอ แต่โชว์เก่า->ใหม่) แล้ว
      // "แปะ" ไว้ด้านบนของแถวเดิม (prepend) เพราะชุดที่เพิ่งโหลดมาเก่ากว่าทุกแถวที่มีอยู่แล้ว
      const displayChunk = [...withBalance].reverse();
      pendingScrollActionRef.current = append ? 'preserve' : 'bottom';
      setRows((prev) => {
        const next = append ? [...displayChunk, ...prev] : displayChunk;
        rowsRef.current = next;
        return next;
      });
      setTotal(data.total);
      setPage(pageToLoad);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      if (append) setLoadingMore(false); else setLoading(false);
    }
  }, [customerId, apiBoundaries, segments, dispatch]);

  // โหลดทุกหน้าให้ครบ (ใช้ตอน export) — คืน { rows, incomeTotal, expenseTotal } ค่าล่าสุด
  const ensureAllLoaded = useCallback(async () => {
    const first = await loadPage(1, false);
    if (!first) throw new Error('โหลดข้อมูลไม่สำเร็จ');
    const totalCount = first.total || 0;
    let pg = 1;
    // eslint-disable-next-line no-constant-condition
    while (realCountRef.current < totalCount) {
      pg += 1;
      // eslint-disable-next-line no-await-in-loop
      const d = await loadPage(pg, true);
      if (!d || !d.rows || d.rows.length === 0) break;
    }
    return {
      rows: rowsRef.current,
      incomeTotal: first.incomeTotal || 0,
      expenseTotal: first.expenseTotal || 0,
    };
  }, [loadPage]);

  useEffect(() => {
    setRows([]);
    rowsRef.current = [];
    setTotal(0);
    runningBalanceRef.current = 0;
    pendingDividersRef.current = [];
    loadPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, apiBoundaries]);

  const statusText = (r) => {
    if (isVerified(r)) return 'ยืนยันแล้ว';
    if (r.ocr_not_found) return 'ไม่พบในรูป';
    return 'ยังไม่ยืนยัน';
  };

  const runExport = async (kind) => {
    setExporting(true);
    setError('');
    try {
      const { rows: full, incomeTotal: incTot, expenseTotal: expTot } = await ensureAllLoaded();
      const fname = `สมุดรายรับ-รายจ่าย_${new Date().toISOString().slice(0, 10)}`;

      if (kind === 'excel') {
        const xlsxMod = await import('xlsx');
        const XLSX = xlsxMod.default || xlsxMod;
        const head = ['วันที่', 'เลขที่เอกสาร', 'รายการ', 'โครงการ', 'รายรับ (บาท)', 'รายจ่าย (บาท)', 'คงเหลือสะสม (บาท)'];
        if (showStatusColumn) head.push('สถานะ');
        const aoa = [head];
        full.forEach((r) => {
          if (r.isDivider) {
            aoa.push([dayKey(r.date), '-', `ยอดตั้งต้น${r.note ? ` — ${r.note}` : ''}`, '-', '', '', Number(r.balance) || 0, ...(showStatusColumn ? ['-'] : [])]);
            return;
          }
          aoa.push([
            dayKey(r.date),
            r.isExternal ? 'ไม่มีในระบบ' : (r.payment_number || ''),
            r.name || '',
            r.project_name || '',
            r.direction === 'income' ? Number(r.amount) || 0 : '',
            r.direction === 'expense' ? Number(r.amount) || 0 : '',
            Number(r.balance) || 0,
            ...(showStatusColumn ? [statusText(r)] : []),
          ]);
        });
        aoa.push(['รวมทั้งหมด', '', '', '', Number(incTot) || 0, Number(expTot) || 0, '', ...(showStatusColumn ? [''] : [])]);
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!cols'] = [{ wch: 12 }, { wch: 16 }, { wch: 38 }, { wch: 24 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 12 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'สมุดบัญชี');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${fname}.xlsx`);
      } else {
        const { default: pdfMake } = await import('addthaifont-pdfmake');
        await import('addthaifont-pdfmake/build/vfs_fonts');
        pdfMake.fonts = {
          Sarabun: {
            normal: 'Sarabun-Light.ttf', bold: 'Sarabun-Regular.ttf',
            italics: 'Sarabun-LightItalic.ttf', bolditalics: 'Sarabun-Italic.ttf',
          },
        };
        const spanRest = showStatusColumn ? 4 : 3;
        const head = ['วันที่', 'เลขที่เอกสาร', 'รายการ', 'โครงการ', 'รายรับ', 'รายจ่าย', 'คงเหลือสะสม']
          .map((t, i) => ({ text: t, bold: true, alignment: i >= 4 ? 'right' : 'left' }));
        if (showStatusColumn) head.push({ text: 'สถานะ', bold: true });
        const body = [head];
        full.forEach((r) => {
          if (r.isDivider) {
            body.push([
              { text: dayKey(r.date) },
              { text: `ยอดตั้งต้น${r.note ? ` — ${r.note}` : ''}`, italics: true, colSpan: 1 + spanRest },
              ...Array.from({ length: spanRest }, () => ({})),
              { text: formatCurrency(r.balance), alignment: 'right', bold: true },
              ...(showStatusColumn ? [{ text: '' }] : []),
            ]);
            return;
          }
          body.push([
            { text: dayKey(r.date) },
            { text: r.isExternal ? 'ไม่มีในระบบ' : (r.payment_number || '') },
            { text: r.name || '-' },
            { text: r.project_name || '-' },
            { text: r.direction === 'income' ? formatCurrency(r.amount) : '', alignment: 'right' },
            { text: r.direction === 'expense' ? formatCurrency(r.amount) : '', alignment: 'right' },
            { text: formatCurrency(r.balance), alignment: 'right' },
            ...(showStatusColumn ? [{ text: statusText(r) }] : []),
          ]);
        });
        body.push([
          { text: 'รวมทั้งหมด', bold: true, colSpan: 4 }, {}, {}, {},
          { text: formatCurrency(incTot), bold: true, alignment: 'right' },
          { text: formatCurrency(expTot), bold: true, alignment: 'right' },
          { text: '' },
          ...(showStatusColumn ? [{ text: '' }] : []),
        ]);
        const widths = showStatusColumn
          ? [50, 58, '*', 78, 52, 52, 62, 48]
          : [52, 64, '*', 92, 58, 58, 68];
        pdfMake.createPdf({
          pageSize: 'A4',
          pageOrientation: 'landscape',
          pageMargins: [20, 36, 20, 28],
          defaultStyle: { font: 'Sarabun', fontSize: 8 },
          content: [
            { text: 'สมุดรายรับ-รายจ่าย', fontSize: 13, bold: true, alignment: 'center', margin: [0, 0, 0, 6] },
            { table: { headerRows: 1, widths, body }, layout: 'lightHorizontalLines' },
          ],
        }).download(`${fname}.pdf`);
      }
    } catch (e) {
      setError(e.message || 'ส่งออกไม่สำเร็จ');
    } finally {
      setExporting(false);
    }
  };

  const hasMore = rows.length < total;
  const colSpan = showStatusColumn ? 8 : 7;

  // ปรับ scrollTop หลัง DOM อัพเดทเสร็จ (ก่อน paint) ทุกครั้งที่ rows/loading เปลี่ยน — โหลด
  // ครั้งแรกเลื่อนไปล่างสุด (โชว์รายการล่าสุด), โหลดเพิ่มจากการเลื่อนขึ้น (prepend) ต้องชดเชย
  // scrollTop ด้วยความสูงที่เพิ่มขึ้นด้านบน ไม่งั้นเนื้อหาที่กำลังดูอยู่จะ "กระโดด" เพราะ
  // browser ไม่ปรับ scrollTop ให้อัตโนมัติเวลาแปะเนื้อหาเพิ่มด้านบน
  //
  // ต้องมี loading อยู่ใน dependency array ด้วย ไม่ใช่แค่ [rows] — เพราะโปรเจกต์นี้ยังใช้
  // React 17 (ไม่มี automatic batching นอก event handler) ใน loadPage: setRows(...) มาก่อน
  // setLoading(false) เป็น 2 setState แยกกันคนละ render จริงๆ (1) setRows → render, loading
  // ยังเป็น true อยู่ → TableContainer ยังไม่ mount (โชว์ CircularProgress แทน) →
  // scrollRef.current เป็น null → effect (ถ้า deps มีแค่ [rows]) จะ return ทิ้งเงียบๆ โดยไม่ได้
  // เลื่อน แล้วก็ไม่รันซ้ำอีกเพราะ rows ไม่ได้เปลี่ยนอีกแล้ว (2) setLoading(false) → render, rows
  // เหมือนเดิม แต่ TableContainer เพิ่งจะ mount จริงตอนนี้ (scrollRef.current มีค่าแล้ว) — ถ้า
  // ไม่มี loading ใน deps effect จะไม่รันซ้ำในรอบนี้ เลยไม่มีจังหวะไหนที่ el ไม่ null พร้อมกับ
  // effect รันเลยสักครั้ง ผลคือค้างบนสุดตลอด ไม่เคยเลื่อนไปล่างสุดสำเร็จ (เจอบั๊กนี้จริงจาก
  // user report — gotcha เดียวกับที่มีคอมเมนต์อธิบายไว้แล้วใน LoanLedgerTable.js)
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const action = pendingScrollActionRef.current;
    if (!el || !action) return;
    if (action === 'bottom') {
      el.scrollTop = el.scrollHeight;
    } else if (action === 'preserve') {
      el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
    }
    pendingScrollActionRef.current = null;
  }, [rows, loading, loadingMore]);

  // เหมือน CustomerCashFlowList — ถ้า render แล้วเนื้อหาไม่ล้นกล่อง (ไม่มี scrollbar)
  // จะไม่มีทาง scroll ขึ้นไปโหลดรายการเก่ากว่าได้เลย ต้อง auto โหลดต่อเองจนกว่าจะล้นหรือหมดจริง
  useEffect(() => {
    if (loading || loadingMore || !hasMore) return;
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight) {
      loadPage(page + 1, true);
    }
  }, [rows, loading, loadingMore, hasMore]);

  // ทิศทางกลับด้าน — เลื่อน "ขึ้น" ไปใกล้บนสุด (ที่ที่รายการเก่าสุดที่โหลดแล้วอยู่) ถึงจะโหลด
  // รายการเก่ากว่าเพิ่ม (ต่างจาก CustomerCashFlowList ที่เลื่อนลงใกล้ล่างสุด)
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || loading || loadingMore || !hasMore) return;
    if (el.scrollTop < SCROLL_TOP_THRESHOLD) {
      loadPage(page + 1, true);
    }
  };

  const tableMaxHeight = fullscreen ? 'calc(100vh - 210px)' : TABLE_MAX_HEIGHT;

  const card = (
    <Paper variant="outlined" sx={{ borderRadius: fullscreen ? 0 : 3, borderColor: '#e2e8f0', overflow: 'hidden', height: fullscreen ? '100%' : 'auto', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: { xs: 2, md: 2.5 }, borderBottom: '1px solid', borderColor: '#e2e8f0', flexShrink: 0 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ width: 42, height: 42, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#ecfeff', color: '#0891b2' }}><LedgerIcon /></Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 750 }}>สมุดรายรับ-รายจ่าย</Typography>
              <Typography variant="body2" color="text.secondary">เรียงจากรายการเก่าไปใหม่ · เลื่อนขึ้นเพื่อดูรายการย้อนหลัง</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={exporting ? <CircularProgress size={14} /> : <ExcelIcon fontSize="small" />}
              onClick={() => runExport('excel')}
              disabled={exporting || loading}
            >
              Excel
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={exporting ? <CircularProgress size={14} /> : <PdfIcon fontSize="small" />}
              onClick={() => runExport('pdf')}
              disabled={exporting || loading}
            >
              PDF
            </Button>
            <Tooltip title={fullscreen ? 'ย่อ' : 'เปิดหน้าต่างใหญ่'}>
              <IconButton onClick={() => setFullscreen((v) => !v)} sx={{ border: '1px solid', borderColor: '#e2e8f0' }}>
                {fullscreen ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="โหลดข้อมูลล่าสุด">
              <span>
                <IconButton onClick={() => loadPage(1, false)} disabled={loading} aria-label="โหลดข้อมูลล่าสุด" sx={{ border: '1px solid', borderColor: '#e2e8f0' }}><RefreshIcon fontSize="small" /></IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
        {exporting && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            กำลังโหลดรายการทั้งหมดเพื่อส่งออก...
          </Typography>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ minHeight: 240, display: 'grid', placeItems: 'center', flex: fullscreen ? 1 : 'none' }}>
          <Stack spacing={1.25} alignItems="center"><CircularProgress size={28} /><Typography variant="body2" color="text.secondary">กำลังโหลดรายการบัญชี...</Typography></Stack>
        </Box>
      ) : (
        <TableContainer
          ref={scrollRef}
          onScroll={handleScroll}
          sx={{ maxHeight: tableMaxHeight, flex: fullscreen ? 1 : 'none', overflowX: 'auto', overflowY: 'auto' }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f1f5f9', color: '#475569', fontWeight: 750, borderColor: '#dbe3ec', py: 1.4, whiteSpace: 'nowrap' } }}>
                <TableCell>วันที่</TableCell>
                <TableCell>เลขที่เอกสาร</TableCell>
                <TableCell>รายการ</TableCell>
                <TableCell>โครงการ</TableCell>
                <TableCell align="right" sx={{ color: '#15803d !important', bgcolor: '#ecfdf5 !important' }}>รายรับ (บาท)</TableCell>
                <TableCell align="right" sx={{ color: '#dc2626 !important', bgcolor: '#fef2f2 !important' }}>รายจ่าย (บาท)</TableCell>
                <TableCell align="right">คงเหลือสะสม (บาท)</TableCell>
                {showStatusColumn && <TableCell>สถานะ</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center">
                    <Box sx={{ py: 5 }}><EmptyIcon sx={{ fontSize: 42, color: '#94a3b8', mb: 0.5 }} /><Typography sx={{ fontWeight: 700 }}>ยังไม่มีรายการบัญชี</Typography><Typography variant="body2" color="text.secondary">รายการรายรับและรายจ่ายจะแสดงที่นี่อัตโนมัติ</Typography></Box>
                  </TableCell>
                </TableRow>
              )}
              {loadingMore && (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center">
                    <CircularProgress size={20} sx={{ my: 1 }} />
                  </TableCell>
                </TableRow>
              )}
              {!hasMore && rows.length > 0 && (
                <TableRow>
                  <TableCell colSpan={colSpan} align="center">
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 1.5 }}>
                      — จุดเริ่มต้นรายการ ({total} รายการ) —
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row, i) => {
                const newDateGroup = i > 0 && dayKey(row.date) !== dayKey(rows[i - 1].date);
                return row.isDivider ? (
                  <TableRow key={row.id} sx={{ bgcolor: '#fff7ed', '& td': { borderTop: newDateGroup ? DATE_GROUP_BORDER : '1px solid #fed7aa', borderBottom: '1px solid #fed7aa' } }}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(row.date).toLocaleDateString('th-TH')}</TableCell>
                    <TableCell>-</TableCell>
                    <TableCell colSpan={2} sx={{ maxWidth: 360 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label="ปรับปรุงยอด" sx={{ bgcolor: '#ffedd5', color: '#9a3412', fontWeight: 700, flexShrink: 0 }} />
                        <Tooltip title={row.note || 'ไม่มีหมายเหตุการปรับปรุง'}>
                          <Typography variant="body2" noWrap sx={{ color: '#9a3412', fontStyle: row.note ? 'normal' : 'italic' }}>
                            {row.note || 'ไม่มีหมายเหตุ'}
                          </Typography>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">-</TableCell>
                    <TableCell align="right">-</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(row.balance)}</TableCell>
                    {showStatusColumn && <TableCell>-</TableCell>}
                  </TableRow>
                ) : (
                  <TableRow
                    key={row.id}
                    onDoubleClick={() => { if (!row.isExternal && row.expenses_id) openDetail(row.expenses_id); }}
                    sx={{ cursor: row.isExternal ? 'default' : 'pointer', '&:nth-of-type(even)': { bgcolor: '#f8fafc' }, '&:hover': { bgcolor: '#eff6ff !important' }, '& td': { borderColor: '#e8edf3', py: 1.25, ...(newDateGroup && { borderTop: DATE_GROUP_BORDER }) } }}
                  >
                    <TableCell sx={{ fontWeight: newDateGroup ? 700 : 400 }}>{new Date(row.date).toLocaleDateString('th-TH')}</TableCell>
                    <TableCell>
                      {row.isExternal ? (
                        <Chip size="small" label="ไม่มีในระบบ" sx={{ bgcolor: '#fef2f2', color: '#b91c1c', fontWeight: 700, height: 22 }} />
                      ) : row.payment_number}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Tooltip title={row.name || '-'}>
                        <Typography variant="body2" noWrap>{row.name || '-'}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 160 }}>
                      <Tooltip title={row.project_name || '-'}>
                        <Typography variant="body2" noWrap>{row.project_name || '-'}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ color: row.direction === 'income' ? '#15803d' : '#94a3b8', fontWeight: 750, bgcolor: row.direction === 'income' ? 'rgba(34,197,94,.05)' : 'transparent' }}>
                      {row.direction === 'income' ? formatCurrency(row.amount) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: row.direction === 'expense' ? '#dc2626' : '#94a3b8', fontWeight: 750, bgcolor: row.direction === 'expense' ? 'rgba(239,68,68,.045)' : 'transparent' }}>
                      {row.direction === 'expense' ? formatCurrency(row.amount) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: '#0f172a' }}>{formatCurrency(row.balance)}</TableCell>
                    {showStatusColumn && (
                      <TableCell>{renderStatusChip(row)}</TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
            {rows.length > 0 && (
              <TableFooter>
                <TableRow
                  sx={{
                    '& td': {
                      position: 'sticky',
                      bottom: 0,
                      zIndex: 3,
                      bgcolor: '#f1f5f9',
                      borderTop: '2px solid #cbd5e1',
                      fontWeight: 800,
                      fontSize: '0.875rem',
                      py: 1.4,
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <TableCell colSpan={4} sx={{ color: '#475569 !important' }}>รวมทั้งหมด</TableCell>
                  <TableCell align="right" sx={{ color: '#15803d !important', bgcolor: '#ecfdf5 !important' }}>
                    {formatCurrency(incomeTotal)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#dc2626 !important', bgcolor: '#fef2f2 !important' }}>
                    {formatCurrency(expenseTotal)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#94a3b8 !important' }}>—</TableCell>
                  {showStatusColumn && <TableCell />}
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      )}

      <Modal open={detailModalOpen} onClose={closeDetail} closeAfterTransition>
        <Fade in={detailModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '98%', sm: '95%', md: '90%', lg: '82%' },
              maxHeight: '95vh',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'primary.main',
                color: 'white',
                flexShrink: 0,
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                รายละเอียดรายการเบิกจ่าย
              </Typography>
              <IconButton size="small" onClick={closeDetail} sx={{ color: 'white' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ overflowY: 'auto', flex: 1, p: { xs: 1, sm: 2 } }}>
              {detailExpenseId && (
                <ExpensesDetail propId={detailExpenseId} onClose={closeDetail} />
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Paper>
  );

  return fullscreen ? (
    <>
      <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: '#e2e8f0', p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">สมุดรายรับ-รายจ่าย เปิดอยู่ในหน้าต่างใหญ่</Typography>
        <Button size="small" onClick={() => setFullscreen(false)} sx={{ mt: 1 }}>กลับมาที่หน้านี้</Button>
      </Paper>
      <Dialog open fullScreen onClose={() => setFullscreen(false)}>
        <Box sx={{ p: { xs: 1, sm: 2 }, height: '100%', bgcolor: '#f1f5f9', boxSizing: 'border-box' }}>
          {card}
        </Box>
      </Dialog>
    </>
  ) : card;
}

export default CompanyLedgerTable;
